import {
    Component,
    DestroyRef,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { filter } from 'rxjs';
import { MatButtonAvailableType } from '../../enums/mat-button-types';
import { MatDialogWidth } from '../../enums/mat-dialog-width';
import { MatThemeColor } from '../../enums/mat-theme-color';
import {
    ConfirmationModalAction,
    ConfirmationModalModel,
} from '../../models/confirmation-modal-model';
import { SharedDialogService } from '../../services/shared-dialog.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

/**
 * Component 'ConfirmationDialogComponent' is intended to replace the 'ConfirmationToastComponent'.
 *
 * The goal is to implement Angular Material where possible.
 * Here, the functionality of 'ConfirmationToastComponent' will be replaced by using Dialog component from Angular Material.
 *
 * When using {@link loading} with {@link MatButtonAvailableType.ICON} or
 * {@link MatButtonAvailableType.MINI_FAB}, pass {@link busyAriaLabel} or
 * {@link ariaLabelButton} so the control keeps an accessible name.
 */
@Component({
    selector: 'apcam-confirmation',
    templateUrl: './confirmation.component.html',
    styleUrls: ['./confirmation.component.scss'],
    imports: [LoadingSpinnerComponent, MatButtonModule, MatIconModule],
})
export class ConfirmationComponent {
    // #region Component I/O
    /**
     * Angular material color of the button
     *
     * @default MatThemeColor.PRIMARY
     * */
    @Input()
    AMColor: MatThemeColor = MatThemeColor.PRIMARY;

    /**
     * Aria-description property for the main button, which opens the confirmation
     *
     * @default null
     */
    @Input()
    ariaDescriptionButton: string | null = null;

    /**
     * Aria-label property for the main button, which opens the confirmation
     *
     * @default null
     */
    @Input()
    ariaLabelButton: string | null = null;

    /**
     * Aria-label used while {@link loading} is true.
     * Falls back to {@link ariaLabelButton} when null.
     *
     * @default null
     */
    @Input()
    busyAriaLabel: string | null = null;

    /**
     * CSS class for the main button, which opens the confirmation
     *
     * @default ''
     */
    @Input()
    buttonClass: string = '';

    /**
     * Text inside the main button, which opens the confirmation
     *
     * @default ''
     */
    @Input()
    buttonText: string = '';

    /**
     * Type of the button.
     *
     * @default MatButtonAvailableType.FLAT
     */
    @Input()
    buttonType: MatButtonAvailableType = MatButtonAvailableType.FLAT;

    /**
     * Description of the confirm confirmation
     *
     * @default ''
     */
    @Input()
    detail: string = '';

    /**
     * Whether the main button is disabled
     *
     * @default false
     */
    @Input()
    disabled: boolean = false;

    /**
     * Whether the main button is extended
     *
     * @description Has effect only if the 'buttonType' property is set to "MatButtonAvailableType.FAB"
     * @default false
     */
    @Input()
    fabExtended: boolean = false;

    /**
     * Height of the modal
     *
     * @default ''
     */
    @Input()
    height: string = '';

    /**
     * Name of the icon to be displayed within the main button
     *
     * @default null
     */
    @Input()
    iconName: string | null = null;

    /**
     * Shows a spinner and marks the button as busy while a confirmed action runs.
     *
     * @default false
     */
    @Input()
    loading: boolean = false;

    /**
     * Max height of the confirmation
     *
     * @default ''
     */
    @Input()
    maxHeight: string = '';

    /**
     * Max width of the confirmation
     *
     * @default ''
     */
    @Input()
    maxWidth: string = '';

    /**
     * Min height of the confirmation
     *
     * @default ''
     */
    @Input()
    minHeight: string = '';

    /**
     * Min width of the confirmation
     *
     * @default ''
     */
    @Input()
    minWidth: string = '';

    /**
     * Summary of the confirm confirmation
     *
     * @default ''
     */
    @Input()
    summary: string = '';

    /**
     * Width of the confirmation
     *
     * @default MatDialogWidth.XS
     */
    @Input()
    width: MatDialogWidth = MatDialogWidth.XS;

    /**
     * Emits when the 'Confirm' button is pressed
     */
    @Output()
    confirm: EventEmitter<void> = new EventEmitter();

    /**
     * Emits when the 'Reject' button is pressed
     */
    @Output()
    reject: EventEmitter<void> = new EventEmitter();

    //#endregion

    MatButtonAvailableType = MatButtonAvailableType;

    protected get isInteractionDisabled(): boolean {
        return this.disabled || this.loading;
    }

    protected get effectiveAriaLabel(): string | null {
        if (this.loading && this.busyAriaLabel) {
            return this.busyAriaLabel;
        }

        return this.ariaLabelButton;
    }

    protected get effectiveAriaBusy(): boolean | null {
        return this.loading ? true : null;
    }

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly dialogService: SharedDialogService
    ) {}

    protected openDialog(): void {
        if (this.isInteractionDisabled) {
            return;
        }

        const dialogData: ConfirmationModalModel = {
            summary: this.summary,
            detail: this.detail,
        };

        const dialogRef = this.dialogService.open<
            ConfirmationDialogComponent,
            ConfirmationModalModel,
            ConfirmationModalAction
        >(ConfirmationDialogComponent, {
            width: this.width,
            height: this.height,
            minWidth: this.minWidth,
            minHeight: this.minHeight,
            maxWidth: this.maxWidth,
            maxHeight: this.maxHeight,
            data: dialogData,
        });

        // React to user selection - trigger appropriate action
        dialogRef
            .afterClosed()
            .pipe(
                filter((data) => !!data),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (data) => {
                    switch (data) {
                        case ConfirmationModalAction.CONFIRM:
                            this.confirm.emit();
                            break;
                        case ConfirmationModalAction.REJECT:
                            this.reject.emit();
                            break;
                    }
                },
            });
    }
}
